import { isEmpty } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { adminLeftMenu } from 'src/layouts/configs';
import { DashboardLayout } from 'src/layouts/dashboard';
import { Permission, PermissionData } from '../../entities/permission.entity';
import { SettingsDrawer } from '../../../../components/settings';
import { hasPermission } from '../../utils/has-permission';
import SessionSync from '../SessionSync';
import StepUpDialog from '../StepUp/StepUpDialog';

/**
 * Check if user has permission for a specific resource and action.
 * @param permissions - List of user permissions
 * @param resource - Resource to check (e.g., 'users')
 * @param action - Action to check (e.g., 'read')
 */

export const AdminLayout = ({ children }: { children: ReactNode }) => {
  const { data } = usePermissions<PermissionData>();
  const translate = useTranslate();
  const { permissions = [] } = data || {};

  // console.log('AdminLayout permissions==', permissions);

  const menus = useMemo(() => {
    /**
     * The menu nests up to three levels, so filtering has to recurse. A grouping
     * item carries no `resource` of its own and should survive only while
     * something below it does — passing it to `hasPermission` instead lets it
     * through unconditionally (that helper reads a missing resource as
     * "unguarded"), and its children then render without ever being checked.
     */
    const filterItems = (items: any[]): any[] =>
      items
        .map((item) => {
          if (Array.isArray(item?.children)) {
            const children = filterItems(item.children);
            return children.length
              ? { ...item, children, tooltip: item.tooltip ? translate(item.tooltip) : undefined }
              : null;
          }
          // an entry that also opens for another resource (`alsoResources`): the helper is not passed the extra field on to the nav
          const { alsoResources, ...entry } = item ?? {};
          const shown =
            hasPermission(permissions, item?.resource, item?.action) ||
            (alsoResources ?? []).some((resource: string) =>
              hasPermission(permissions, resource, item?.action)
            );
          // the nav shows a tooltip as given (it translates only the title), so it is translated here
          return shown
            ? { ...entry, tooltip: entry.tooltip ? translate(entry.tooltip) : undefined }
            : null;
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

    return adminLeftMenu
      .map(({ items, ...section }) => ({ ...section, items: filterItems(items || []) }))
      .filter(({ items }) => !isEmpty(items));
  }, [permissions, translate]);

  // console.log('AdminLayout menus==', menus, permissions);

  return (
    <DashboardLayout
      data={{
        nav: menus,
      }}
    >
      <SessionSync />
      <StepUpDialog />
      {children}
      <SettingsDrawer hideFont />
    </DashboardLayout>
  );
};
